import { createContext, PropsWithChildren, useEffect, useState } from "react"
import { Cart, MainProduct, Screen } from "../../types"
import { areArraysEqual, getActiveMaterialByMainProductVariantId, getInitialActivePendantProductVariantIds, isEditMode, isPos, loadActivePendantsFromCookies, saveActivePendantsToCookies } from "../../utils"
import { getCorrespondingPendantProductVariantIdsByMaterial, getInitialEditProductVariantIds, getInitialUrlQueryProductVariantIds, updateUrlQueryProductVariantIds } from "./utils"

type AppContext = {
  mainProduct: MainProduct
  cart: Cart
  activeScreen: Screen
  setActiveScreen: (screen: Screen) => void
  activeMainProductVariantId: string,
  setActiveMainProductVariantId: (id: string) => void
  activePendantProductVaraintIds: string[]
  setActivePendantProductVaraintIds: (id: string[]) => void
  active: number
  setActive: (index: number) => void
  activePendantProductId: string
  setActivePendantProductId: (activePendantProductId: string) => void
  designSummaryPopupOpen: boolean
  setDesignSummaryPopupOpen: (designSummaryPopupOpen: boolean) => void
  reset: () => void
}

const initialValue: AppContext = {
  mainProduct: {
    id: 0,
    title: "",
    tags: [],
    collectionUrl: "",
    variants: [],
    optionsWithValues: [],
    metafields: {
      type: "Necklace",
      pdpBiLine: "",
      pendantProducts: []
    }
  },
  cart: { items: [] },
  activeScreen: 'CHAIN', 
  setActiveScreen: () => null,
  activeMainProductVariantId: "",
  setActiveMainProductVariantId: () => null,
  activePendantProductVaraintIds: [],
  setActivePendantProductVaraintIds: () => null,
  active: 0,
  setActive: () => null,
  activePendantProductId: "",
  setActivePendantProductId: () => null,
  designSummaryPopupOpen: false,
  setDesignSummaryPopupOpen: () => null,
  reset: () => null
}

export const AppContext = createContext<AppContext>(initialValue)

type Props = PropsWithChildren & {
  mainProduct: MainProduct
  cart: Cart
}

export const AppProvider = ({ children, mainProduct, cart }: Props) => {
  const initialScreen: Screen = 'PENDANTS'

  const initialUrlQueryProductVariantIds = getInitialUrlQueryProductVariantIds(mainProduct)
  const initialEditProductVariantIds = getInitialEditProductVariantIds(cart)

  const initialActiveMainProductVariantId = mainProduct.variants[0].id
  
  const initialActivePendantProductId = mainProduct.metafields.pendantProducts[0].id
  const initialPendantVariantIds = getInitialActivePendantProductVariantIds(mainProduct, initialActiveMainProductVariantId)  
  const initialSelectedPendantProductIndex = initialPendantVariantIds.length - 1

  const userPendantVariantIds = getInitialActivePendantProductVariantIds(mainProduct, initialActiveMainProductVariantId)  
  const posStoragePendantVariantIds = loadActivePendantsFromCookies(mainProduct.id)?.pendantIds ?? userPendantVariantIds
  const posStorageMainProductVariantId = loadActivePendantsFromCookies(mainProduct.id)?.chainId ?? mainProduct.variants[0].id

  const [activeScreen, setActiveScreen] = useState<Screen>(initialScreen)
  const [activeMainProductVariantId, setActiveMainProductVariantId] = useState(
    isEditMode()
      ? initialEditProductVariantIds!.chain
      : isPos() 
        ? posStorageMainProductVariantId 
        : initialUrlQueryProductVariantIds
          ? initialUrlQueryProductVariantIds.chain
          : initialActiveMainProductVariantId
  )
  const [activePendantProductId, setActivePendantProductId] = useState(initialActivePendantProductId)
  const [activePendantProductVaraintIds, setActivePendantProductVaraintIds] = useState<string[]>(
    isEditMode()
      ? initialEditProductVariantIds!.pendants
      : isPos() 
        ? posStoragePendantVariantIds 
        : initialUrlQueryProductVariantIds
          ? initialUrlQueryProductVariantIds.pendants
          : userPendantVariantIds
  )

  const [selectedPendantProductIndex, setSelectedPendantProductIndex] = useState<number>(initialSelectedPendantProductIndex)
  const [designSummaryPopupOpen, setDesignSummaryPopupOpen] = useState(false)

  const reset = () => {
    setSelectedPendantProductIndex(initialSelectedPendantProductIndex)
    setActiveMainProductVariantId(initialActiveMainProductVariantId)
    setActivePendantProductVaraintIds(initialPendantVariantIds)
    setActiveScreen('PENDANTS')
  }

  useEffect(() => {
    const activeMaterial = getActiveMaterialByMainProductVariantId(mainProduct, activeMainProductVariantId)

    const correspondingPendantProductVariantIdsByMaterial = getCorrespondingPendantProductVariantIdsByMaterial(
      mainProduct, 
      activePendantProductVaraintIds, 
      activeMaterial
    )

    const pendantVariantIdsWillChange = !areArraysEqual(correspondingPendantProductVariantIdsByMaterial, activePendantProductVaraintIds)
    updateUrlQueryProductVariantIds(activeMainProductVariantId, activePendantProductVaraintIds)
    
    if (pendantVariantIdsWillChange) {
      setActivePendantProductVaraintIds(correspondingPendantProductVariantIdsByMaterial)
    }
  }, [activeMainProductVariantId])

  useEffect(() => {
    !isPos() && updateUrlQueryProductVariantIds(activeMainProductVariantId, activePendantProductVaraintIds)
    isPos() && saveActivePendantsToCookies(mainProduct.id, activeMainProductVariantId, activePendantProductVaraintIds)
  }, [activePendantProductVaraintIds])

  return (
    <AppContext.Provider value={{
      mainProduct,
      cart,
      activeScreen,
      setActiveScreen,
      activeMainProductVariantId,
      setActiveMainProductVariantId,
      activePendantProductVaraintIds,
      setActivePendantProductVaraintIds,
      active: selectedPendantProductIndex,
      setActive: setSelectedPendantProductIndex,
      activePendantProductId,
      setActivePendantProductId,
      designSummaryPopupOpen,
      setDesignSummaryPopupOpen,
      reset
    }}>
      { children }
    </AppContext.Provider>
  )
}
